import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Button, Form, Row, Col, Badge, Alert as BsAlert } from "react-bootstrap";
import SafetyLicenzeService from "../../../services/safety/SafetyLicenzeService";

const EMPTY = {
    id_tipo_lic: "",
    titolo: "",
    numero: "",
    ente_rilascio: "",
    data_rilascio: "",
    data_scadenza: "",
    periodicita_mesi: "",
    note: "",
};

const LIC_HELP = {
    AUT_VIGILANZA: "Autorizzazione prefettizia all'esercizio del servizio di vigilanza privata. Rilascio = data del provvedimento. Di norma senza scadenza fissa: resta valida finché non revocata.",
    AUT_INVESTIGAZIONI: "Autorizzazione prefettizia art. 134 TULPS per lo svolgimento di attività investigative. Rilascio = data del decreto/autorizzazione.",
    REG_SERVIZIO: "Regolamento interno che disciplina modalità operative, divise, comportamento del personale. Va aggiornato a fronte di modifiche organizzative.",
    POLIZZA_RC: "Polizza di responsabilità civile professionale obbligatoria per vigilanza/investigazioni. Emissione = data di decorrenza; scadenza = fine validità polizza (tipicamente annuale).",
    CERT_ISO9001: "Certificazione del sistema di gestione per la qualità. Emissione = data certificato; scadenza = prossima sorveglianza/rinnovo (tipicamente 3 anni).",
    CERT_ISO45001: "Certificazione del sistema di gestione per la salute e sicurezza sul lavoro.",
    CERT_UNI10459: "Schema di certificazione UNI/PdR 104:2019 per i servizi di vigilanza privata.",
    CERT_UNI10677: "Schema di certificazione UNI 10677 per le investigazioni private.",
    DURC: "Documento che attesta la regolarità contributiva. Validità tipica: 120 giorni (4 mesi).",
};

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
}

/** Raggruppa i tipi catalogo per categoria (Autorizzazioni, Regolamenti, Assicurazioni…). */
function groupByCategoria(tipi) {
    const groups = {};
    tipi.forEach(t => {
        const cat = t.categoria || "Altri";
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(t);
    });
    return Object.entries(groups);
}

/**
 * LicenzePanel
 * Pannello per autorizzazioni prefettizie, regolamenti di servizio, polizze
 * assicurative e certificazioni UNI/ISO a livello azienda (non solo "licenze"
 * nel senso stretto TULPS). Catalogo filtrato per settore.
 *
 * Props:
 *  - idAzienda: number (richiesto)
 *  - settore: string opzionale (CSV comparti, sovrascrive derivazione da azienda)
 */
export default function LicenzePanel({ idAzienda, settore }) {
    const [tipi, setTipi] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const loadTipi = useCallback(async () => {
        if (!idAzienda) { setTipi([]); return; }
        try {
            // Il settore viene derivato lato server dall'azienda (più affidabile
            // del prop frontend). Si passa settore solo come override esplicito.
            const resp = await SafetyLicenzeService.getTipi(idAzienda);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            if (Array.isArray(data)) {
                setTipi(data);
            } else if (data?.message || data?.error) {
                setTipi([]);
                setError(data.message || data.error);
            } else {
                setTipi([]);
            }
        } catch (err) {
            setTipi([]);
            setError(err?.message || "Impossibile caricare il catalogo titoli. Verifica che lo script SQL e i file backend siano stati deployati.");
        }
    }, [idAzienda]);

    const loadItems = useCallback(async () => {
        if (!idAzienda) { setItems([]); return; }
        setLoading(true);
        setError(null);
        try {
            const resp = await SafetyLicenzeService.byAzienda(idAzienda);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err?.message || "Errore nel caricamento dei titoli aziendali.");
        } finally {
            setLoading(false);
        }
    }, [idAzienda]);

    useEffect(() => { loadTipi(); }, [loadTipi]);
    useEffect(() => { loadItems(); }, [loadItems]);

    const openForm = () => {
        setForm(EMPTY);
        setFile(null);
        setError(null);
        setMsg(null);
        setShow(true);
    };

    const submit = async () => {
        if (!idAzienda) return;
        if (!form.id_tipo_lic && !form.titolo) {
            setError("Seleziona un tipo o indica un titolo.");
            return;
        }
        setSaving(true);
        setError(null);
        setMsg(null);
        try {
            const fd = new FormData();
            fd.append("id_azienda", idAzienda);
            if (form.id_tipo_lic)     fd.append("id_tipo_lic", form.id_tipo_lic);
            if (form.titolo)          fd.append("titolo", form.titolo);
            if (form.numero)          fd.append("numero", form.numero);
            if (form.ente_rilascio)   fd.append("ente_rilascio", form.ente_rilascio);
            if (form.data_rilascio)    fd.append("data_rilascio", form.data_rilascio);
            if (form.data_scadenza)    fd.append("data_scadenza", form.data_scadenza);
            if (form.periodicita_mesi) fd.append("periodicita_mesi", form.periodicita_mesi);
            if (form.note)             fd.append("note", form.note);
            if (file)                  fd.append("file", file);
            await SafetyLicenzeService.create(fd);
            setMsg("Titolo aziendale aggiunto.");
            setShow(false);
            setForm(EMPTY);
            setFile(null);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio del titolo.");
        } finally {
            setSaving(false);
        }
    };

    const onDownload = async (l) => {
        setError(null);
        try {
            const fn = `${(l.titolo || l.tipo_denominazione || "titolo").toString().replace(/[^\w\-]+/g, "_")}.pdf`;
            await SafetyLicenzeService.download(l.id_licenza, fn);
        } catch (err) {
            setError(err?.message || "Impossibile scaricare il file.");
        }
    };

    const onDelete = async (l) => {
        if (!l?.id_licenza) return;
        if (!window.confirm("Eliminare questo titolo aziendale?")) return;
        setError(null);
        try {
            await SafetyLicenzeService.deleteLicenza(l.id_licenza);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nell'eliminazione.");
        }
    };

    const tipoSel = form.id_tipo_lic ? tipi.find(t => Number(t.id_tipo_lic) === Number(form.id_tipo_lic)) : null;
    const tipoHelp = tipoSel ? LIC_HELP[tipoSel.codice] : null;

    const presentTipoIds = new Set(items.filter(i => i.id_tipo_lic).map(i => Number(i.id_tipo_lic)));
    const mancanti = tipi.filter(t => Number(t.obbligatorio) === 1 && !presentTipoIds.has(Number(t.id_tipo_lic)));

    if (!idAzienda) {
        return (
            <BsAlert variant="info" className="py-2 mb-0">
                <i className="bi bi-info-circle me-1" />
                Salva prima i dati dell'azienda per gestire autorizzazioni, polizze e certificazioni.
            </BsAlert>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                    <i className="bi bi-award me-2 text-primary" />
                    Autorizzazioni, polizze e certificazioni
                </h6>
                <Button size="sm" variant="primary" onClick={openForm} disabled={show}>
                    <i className="bi bi-plus-lg me-1" />
                    Aggiungi titolo
                </Button>
            </div>

            <div className="text-muted small mb-2">
                Qui si archiviano <strong>autorizzazioni prefettizie</strong> (art. 134/138 TULPS),
                <strong> regolamenti di servizio</strong>, <strong>polizze assicurative</strong> (RC, INAIL)
                e <strong>certificazioni</strong> (ISO, UNI). Non sono tutti «licenze» nel senso stretto:
                la select è organizzata per categoria. Il catalogo dipende dal comparto dell'azienda.
            </div>

            {mancanti.length > 0 && (
                <BsAlert variant="warning" className="py-2 small">
                    <i className="bi bi-exclamation-triangle me-1" />
                    <strong>Titoli obbligatori mancanti ({mancanti.length}):</strong>{" "}
                    {mancanti.map(m => m.denominazione).join(", ")}.
                </BsAlert>
            )}

            {show && tipi.length === 0 && !error && (
                <BsAlert variant="warning" className="py-2 small mb-2">
                    <i className="bi bi-exclamation-triangle me-1" />
                    Catalogo titoli vuoto. Assicurati di aver eseguito lo script SQL
                    <code className="mx-1">2026-06-08_safety_licenze.sql</code>
                    e di aver selezionato i <strong>comparti</strong> dell'azienda nel tab Dati azienda.
                </BsAlert>
            )}

            {show && (
                <div className="border rounded p-3 mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                    <div className="fw-semibold mb-2">
                        <i className="bi bi-award-fill me-1 text-primary" />
                        Nuovo titolo aziendale
                    </div>
                    <Row className="g-2">
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Categoria / tipo</Form.Label>
                            <Form.Select
                                size="sm"
                                value={form.id_tipo_lic}
                                onChange={e => setForm(f => ({ ...f, id_tipo_lic: e.target.value }))}
                            >
                                <option value="">— Nessuno / altro —</option>
                                {groupByCategoria(tipi).map(([cat, items]) => (
                                    <optgroup key={cat} label={cat}>
                                        {items.map(t => (
                                            <option key={t.id_tipo_lic} value={t.id_tipo_lic}>
                                                {t.denominazione}{Number(t.obbligatorio) === 1 ? " (obbligatorio)" : ""}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Autorizzazioni TULPS, regolamenti, assicurazioni o certificazioni UNI/ISO.
                            </Form.Text>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Titolo</Form.Label>
                            <Form.Control
                                size="sm"
                                value={form.titolo}
                                placeholder={tipoSel ? tipoSel.denominazione : "Es. Autorizzazione vigilanza - Prefettura di Roma"}
                                onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))}
                            />
                        </Col>

                        {tipoSel && (
                            <Col xs={12}>
                                <BsAlert variant="info" className="py-2 mb-1 small">
                                    <div className="fw-semibold">
                                        <i className="bi bi-lightbulb me-1" />
                                        {tipoSel.denominazione}
                                        {tipoSel.riferimento_normativo
                                            ? <span className="text-muted fw-normal"> — {tipoSel.riferimento_normativo}</span>
                                            : null}
                                    </div>
                                    {tipoHelp || "Indica la data del provvedimento, polizza o certificato. La scadenza è facoltativa se il titolo non ha termine."}
                                </BsAlert>
                            </Col>
                        )}

                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Numero / protocollo</Form.Label>
                            <Form.Control size="sm" value={form.numero} onChange={e => setForm(f => ({ ...f, numero: e.target.value }))} />
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Ente di rilascio</Form.Label>
                            <Form.Control
                                size="sm"
                                placeholder={tipoSel?.ente_tipico || "Es. Prefettura"}
                                value={form.ente_rilascio}
                                onChange={e => setForm(f => ({ ...f, ente_rilascio: e.target.value }))}
                            />
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Data rilascio</Form.Label>
                            <Form.Control size="sm" type="date" value={form.data_rilascio} onChange={e => setForm(f => ({ ...f, data_rilascio: e.target.value }))} />
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Periodicità (mesi)</Form.Label>
                            <Form.Control size="sm" type="number" min="0" placeholder="es. 12" value={form.periodicita_mesi} onChange={e => setForm(f => ({ ...f, periodicita_mesi: e.target.value }))} />
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label className="small mb-0">Scadenza (opz.)</Form.Label>
                            <Form.Control size="sm" type="date" value={form.data_scadenza} onChange={e => setForm(f => ({ ...f, data_scadenza: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={5}>
                            <Form.Label className="small mb-0">File (PDF / scansione)</Form.Label>
                            <Form.Control size="sm" type="file" accept="application/pdf,image/*" onChange={e => setFile(e.target.files?.[0] || null)} />
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small mb-0">Note</Form.Label>
                            <Form.Control as="textarea" rows={2} size="sm" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end gap-2 mt-2">
                        <Button size="sm" variant="secondary" onClick={() => setShow(false)} disabled={saving}>Annulla</Button>
                        <Button size="sm" variant="primary" onClick={submit} disabled={saving}>
                            {saving ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-check2 me-1" />}
                            Salva titolo
                        </Button>
                    </div>
                </div>
            )}

            {error && <BsAlert variant="danger" className="py-2">{error}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2">{msg}</BsAlert>}

            {loading ? (
                <div className="text-muted small"><Spinner size="sm" animation="border" className="me-2" />Caricamento titoli aziendali…</div>
            ) : items.length === 0 ? (
                <div className="text-muted small">
                    Nessun titolo registrato. Usa <strong>"Aggiungi titolo"</strong> per archiviare
                    autorizzazioni, polizze, regolamenti e certificazioni.
                </div>
            ) : (
                <Table size="sm" bordered hover responsive className="mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Titolo</th>
                            <th>Numero</th>
                            <th>Rilascio</th>
                            <th>Scadenza</th>
                            <th>Stato</th>
                            <th className="text-end">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(l => {
                            const stato = l.stato || "PERMANENTE";
                            const badge = stato === "SCADUTO" ? "danger"
                                : stato === "IN_SCADENZA" ? "warning"
                                : stato === "VALIDO" ? "success" : "light";
                            const badgeText = (stato === "SCADUTO" || stato === "VALIDO") ? "light" : "dark";
                            const label = stato === "IN_SCADENZA" ? "In scadenza"
                                : stato === "PERMANENTE" ? "Senza scadenza"
                                : stato.charAt(0) + stato.slice(1).toLowerCase();
                            return (
                                <tr key={l.id_licenza}>
                                    <td>
                                        <div>{l.titolo || l.tipo_denominazione || "—"}</div>
                                        <div className="text-muted small">
                                            {l.tipo_categoria || ""}
                                            {l.ente_rilascio ? `${l.tipo_categoria ? " · " : ""}${l.ente_rilascio}` : ""}
                                        </div>
                                    </td>
                                    <td>{l.numero || <span className="text-muted">—</span>}</td>
                                    <td>{fmtDate(l.data_rilascio)}</td>
                                    <td>{fmtDate(l.data_scadenza)}</td>
                                    <td>
                                        <Badge bg={badge} text={badgeText} className={badge === "light" ? "border" : undefined}>{label}</Badge>
                                        {typeof l.giorni_alla_scadenza === "number" && (stato === "SCADUTO" || stato === "IN_SCADENZA") && (
                                            <span className="text-muted small ms-1">
                                                {l.giorni_alla_scadenza < 0
                                                    ? `${Math.abs(l.giorni_alla_scadenza)} gg fa`
                                                    : `tra ${l.giorni_alla_scadenza} gg`}
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-end">
                                        {l.path_file ? (
                                            <Button size="sm" variant="danger" className="me-1" onClick={() => onDownload(l)} title="Scarica file">
                                                <i className="bi bi-file-earmark-pdf me-1" />PDF
                                            </Button>
                                        ) : null}
                                        <Button size="sm" variant="outline-danger" onClick={() => onDelete(l)} title="Elimina">
                                            <i className="bi bi-trash" />
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
